"use client";

import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo, useRef, useCallback } from "react";
import { Client, Room } from "colyseus.js";
import { GameState } from "@/lib/types/common/GameState";
import type { GameContext } from "@/lib/types/GameContext";
import { GameStep } from "@/lib/types/common/GameStep";
import { getEnvVar } from "@/lib/utils/getEnvVar";

const GameContext = createContext<GameContext>({
    client: null,
    room: null,
    gameState: null,
    setRoom: () => { },
    leaveRoom: () => { },
    players: [],
    showWinnerScreen: false,
    setShowWinnerScreen: () => { },
    photosphereLoading: false,
    setPhotosphereLoading: () => { }
});

export function GameProvider({ children }: { children: ReactNode }) {
    const [client] = useState(() => new Client(getEnvVar("NEXT_PUBLIC_COLYSEUS_SERVER_URL")));
    const [room, setRoom] = useState<Room<GameState> | null>(null);
    const [gameState, setGameState] = useState<GameState | null>(null);
    const [showWinnerScreen, setShowWinnerScreen] = useState<boolean>(true);
    const [photosphereLoading, setPhotosphereLoading] = useState<boolean>(false);
    const isIntentionalLeaveRef = useRef<boolean>(false);
    
    const leaveRoom = useCallback(() => {
        isIntentionalLeaveRef.current = true;
        room?.leave();
    }, [room]);
    
    const players = useMemo(() => {
        return Array.from(gameState?.players?.values?.() ?? []);
    }, [gameState]);

    useEffect(() => {
        if (room && typeof window !== 'undefined') {
            const url = `${window.location.pathname}?id=${room.roomId}`;
            window.history.replaceState({}, '', url);
            // Reset intentional leave flag when joining a new room
            isIntentionalLeaveRef.current = false;
        }
    }, [room]);

    useEffect(() => {
        if (!room) return;

        // Set initial state immediately from room.state to avoid race condition
        // where onStateChange might miss the initial state
        if (room.state) {
            const clonedInitial = Object.create(
                GameState.prototype,
                Object.getOwnPropertyDescriptors(room.state)
            );
            setGameState(clonedInitial);
        }

        room.onStateChange((newState) => {

            const cloned = Object.create(
                GameState.prototype,
                Object.getOwnPropertyDescriptors(newState)
            );

            setGameState(cloned);
        });

        // Handle player info updates for localStorage storage
        room.onMessage("playerInfoUpdated", (data) => {

            // Store player info in localStorage for reconnection
            if (typeof window !== 'undefined') {
                localStorage.setItem('playerName', data.name);
                localStorage.setItem('playerAvatarId', data.avatarId);
                localStorage.setItem('playerUniqueId', data.uniqueId);
            }
        });

        // Handle room disconnect/close - alert user and reload page only for unexpected disconnections
        room.onLeave((code) => {
            console.log("🚪 Room left with code:", code, "Intentional:", isIntentionalLeaveRef.current);
            // Only show alert if this was NOT an intentional leave
            if (!isIntentionalLeaveRef.current && typeof window !== 'undefined') {
                alert("The game room has been closed unexpectedly. The page will reload.");
                window.location.reload();
            }
            // Reset for next room
            isIntentionalLeaveRef.current = false;
        });

        return () => {
            isIntentionalLeaveRef.current = true;
            room.leave();
        };
    }, [room]);

    // Manage winner screen state based on game step changes
    const [previousGameStep, setPreviousGameStep] = useState<GameStep | undefined>();
    useEffect(() => {
        if (gameState?.gameStep !== previousGameStep) {
            setPreviousGameStep(gameState?.gameStep);
            // When entering RECAP, show winner screen first
            if (gameState?.gameStep === GameStep.RECAP && previousGameStep !== GameStep.RECAP) {
                setShowWinnerScreen(true);
            }
        }
    }, [gameState?.gameStep, previousGameStep]);

    // Manage photosphere loading state
    useEffect(() => {
        if (gameState?.gameStep === GameStep.GUESSING) {
            // Set loading to true when entering guessing phase
            setPhotosphereLoading(true);
        } else {
            // Reset loading when leaving guessing phase
            setPhotosphereLoading(false);
        }
    }, [gameState?.gameStep, gameState?.currentPhotosphere?.id]);

    return (
        <GameContext.Provider value={{ client, room, gameState: gameState, setRoom, leaveRoom, players, showWinnerScreen, setShowWinnerScreen, photosphereLoading, setPhotosphereLoading }}>
            {children}
        </GameContext.Provider>
    );
}

export function useGame(): GameContext {
    return useContext(GameContext);
}